'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '@/shared/lib/cn';
import { useOptionalDesktopLayout } from '@/features/project';
import { useBreakpoint } from '@/shared/hooks/use-media-query';
import {
  fittedImageHeightCap,
  imageBoxMaxWidth,
  imageFrameStripPadding,
  IMAGE_FRAME_STRIP_PADDING,
  type ImageSizingConstraints,
} from '@/shared/hooks';

/**
 * Height cap the frame measured for its own content column, published to the
 * image inside it. `null` outside a two-column layout (or before the first
 * measurement), where the image falls back to the viewport heuristic in
 * `useImageConstraints`.
 */
const AngleImageHeightCapContext = createContext<number | null>(null);

/** Read the column-fitted height cap published by the enclosing frame. */
export function useAngleImageHeightCap(): number | null {
  return useContext(AngleImageHeightCapContext);
}

interface AngleImageFrameProps {
  children: ReactNode;
  className?: string;
  /**
   * Ratio of the image inside (measured, falling back to the API-known angle
   * ratio). Drives the strip padding. Omit for frames that hold a full-width
   * placeholder instead of a ratio-shaped image — those never leave strips.
   */
  imageAspectRatio?: number | null;
  /** Per-layout constraints, used until the column measurement lands. */
  sizing?: ImageSizingConstraints;
}

/**
 * Returns the nearest scrolling ancestor, or null if the element scrolls with
 * the document.
 */
function findScrollParent(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
}

/**
 * Shared outer frame for angle image cards (main and derivative).
 *
 * - Horizontal (two-column xl+ desktop) layout: `rounded-2xl bg-settings-card`
 *   surface, with vertical padding matching the gray strips the centered image
 *   leaves on the sides (capped at 8px) — so a height-bound image reads as a
 *   framed panel instead of a band with strips on two sides only. Apple's
 *   `AngleViewerSection.angleCardNeedsVerticalPadding` is the same rule.
 * - Single-column layout: tight `rounded-lg bg-image-backdrop` frame. On narrow
 *   phones (`< sm`) the frame bleeds to the screen edges (100% width, no page
 *   inset) and drops its border — parity with Apple's full-bleed stacked image
 *   header. Tablet-portrait single-column (`sm`+) keeps the inset, bordered
 *   frame. Only the image frame bleeds; sibling note/feedback banners stay
 *   inset.
 *
 * In the two-column layout the frame also owns the image's HEIGHT CAP, because
 * it is the only component that knows where the image actually sits: it
 * measures its content column and the chrome above itself, and publishes
 * `columnHeight - chromeAbove - gapBelow - reserve` (see `fittedImageHeightCap`)
 * to the image below through context. That is Apple's arrangement too —
 * `ConceptContentView` feeds `calculateMaxImageHeight` the column height read
 * from geometry, not the window height. WEB used to pass `window.innerHeight`
 * into the same formula while the image sat below a topbar and a tab row, so
 * the image always overshot the column by exactly that chrome and the column
 * scrolled a few px on a window that had room to spare.
 *
 * Every input here is independent of the image height (the column is sized by
 * the page shell, the chrome above by the tabs), so nothing feeds back. Two
 * rules keep it that way, and both are load-bearing:
 *   - never derive the cap from anything BELOW the image (that is the image
 *     itself, one step removed);
 *   - never derive a height from a measured WIDTH. The strip padding used to do
 *     that via a ResizeObserver, and it strobed: +16px of padding overflowed the
 *     column, the scrollbar ate ~15px of width, the strips vanished, the padding
 *     switched off, the scrollbar went away, the strips came back. The padding
 *     is now a CSS `clamp()` over a percentage of the frame's own width, which
 *     the browser resolves during layout instead.
 */
export function AngleImageFrame({
  children,
  className,
  imageAspectRatio,
  sizing,
}: AngleImageFrameProps) {
  const desktopLayout = useOptionalDesktopLayout();
  const { isXl } = useBreakpoint();
  const isHorizontalLayout = !!(isXl && desktopLayout);

  const frameRef = useRef<HTMLDivElement>(null);
  const [measuredCap, setMeasuredCap] = useState<number | null>(null);

  const measure = useCallback(() => {
    const el = frameRef.current;
    if (!el || !isHorizontalLayout) {
      setMeasuredCap(null);
      return;
    }
    const scroller = findScrollParent(el);
    if (!scroller) {
      setMeasuredCap(null);
      return;
    }
    const columnHeight = scroller.clientHeight;
    if (columnHeight <= 0) return;
    // Distance from the column's top to this frame: the tab row, the header
    // banners, every gap above. Read live so it stays right when they change.
    const chromeAbove =
      el.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop;
    // The frame's own strip padding sits between its top edge and the image, so
    // it eats into the same budget. Reserve the maximum unconditionally: the
    // actual padding depends on the box WIDTH, and reading that back to decide a
    // height is the loop this component exists to avoid.
    const gapBelow =
      (parseFloat(getComputedStyle(el).marginBottom) || 0) +
      2 * IMAGE_FRAME_STRIP_PADDING;
    const next = fittedImageHeightCap(columnHeight, chromeAbove, gapBelow);
    setMeasuredCap((prev) =>
      prev !== null && Math.abs(prev - next) < 0.5 ? prev : next
    );
  }, [isHorizontalLayout]);

  // After every render (the chrome above can change with the tabs) and on every
  // column resize. Measuring in a layout effect keeps the corrected cap in the
  // same paint, so the image never flashes at the wrong height.
  useLayoutEffect(measure);

  useEffect(() => {
    const el = frameRef.current;
    if (!el || !isHorizontalLayout) return;
    const scroller = findScrollParent(el);
    if (!scroller) return;
    // The column's own box only ever changes with the window / page chrome —
    // never with what is inside it, which is why observing it is safe.
    const ro = new ResizeObserver(measure);
    ro.observe(scroller);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [isHorizontalLayout, measure]);

  const effectiveCap = measuredCap ?? sizing?.maxImageHeight ?? null;
  const paddingBlock =
    isHorizontalLayout && imageAspectRatio && effectiveCap
      ? imageFrameStripPadding(
          imageBoxMaxWidth(imageAspectRatio, {
            maxImageHeight: effectiveCap,
            isSingleColumn: false,
            isReady: true,
          })
        )
      : undefined;

  return (
    <AngleImageHeightCapContext.Provider
      value={isHorizontalLayout ? measuredCap : null}
    >
      <div
        ref={frameRef}
        style={paddingBlock ? { paddingBlock } : undefined}
        className={cn(
          'relative overflow-hidden',
          // Narrow phones (< sm): square the whole card. The image rides full-bleed
          // to the screen edges, where rounded corners read as a mistake. Squares
          // the inner image container too (`[data-image-frame]`, which carries its
          // own `rounded-lg`) so no frame-gray peeks in the rounded corners.
          // `sm`+ (tablet portrait / desktop) keeps the rounded card.
          'max-sm:[&_[data-image-frame]]:rounded-none',
          isHorizontalLayout
            ? 'rounded-2xl bg-settings-card'
            : // Narrow phones bleed to the screen edges (no page inset) and drop
              // the side border so no hairline sits flush against the edge;
              // `sm`+ (tablet portrait) restores the inset + bordered frame.
              'rounded-none sm:rounded-lg bg-image-backdrop -mx-4 sm:mx-0 sm:border sm:border-border',
          className
        )}
      >
        {children}
      </div>
    </AngleImageHeightCapContext.Provider>
  );
}
